/* eslint-disable consistent-return */
import { Meteor } from 'meteor/meteor';
import { check } from 'meteor/check';
import { HTTP } from 'meteor/http';
import { isString, isNil } from 'lodash';

import * as mapServerElementOps from '../map-server-element-operations';
import MapServerElements from '../MapServerElements';

import { isHTTPResultOK } from '../../../modules/fetch-utilites';
import checkAuthentication from '../../../modules/server/check-authentication';
import handleMethodException from '../../../modules/handle-method-exception';

const { getMapServerElementListAPI: MAP_SERVER_ELEMENT_LIST_API } = Meteor.settings.private;

const editMapServerElementSchema = MapServerElements.schema.omit(
  'owner',
  'createdAt',
  'updatedAt',
  'deleted'
);
editMapServerElementSchema.extend({ _id: String });

/**
 * Inserts the map server element in the database.
 *
 * @async
 * @param {object} mapServerElementObject - The map server element object.
 * @param {string} userId - The user id.
 * @returns {string} Return the id of the new map server element.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = async (mapServerElementObject, userId) => {
  check(userId, String);
  try {
    checkAuthentication();
    MapServerElements.schema.validate(mapServerElementObject, {
      check,
      keys: ['name', 'type', 'url']
    });
    return MapServerElements.insert({ owner: userId, ...mapServerElementObject });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the map server element in the database.
 *
 * @param {object} mapServerElementObject - The map server element object.
 * @returns {string} Return the id of the updated map server element.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = mapServerElementObject => {
  try {
    checkAuthentication();
    editMapServerElementSchema.validate(mapServerElementObject);
    const mapServerElementId = mapServerElementOps.getId(mapServerElementObject);
    MapServerElements.update(mapServerElementId, { $set: mapServerElementObject });
    return mapServerElementId;
    // Return _id so we can redirect to map server element after update.
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Copies the map server element in the database.
 *
 * @param {string} mapServerElementId - The map server element id.
 * @param {string} userId - The user id that wants the map server element.
 * @returns {string} Return the id of the new map server element copied.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const copy = (mapServerElementId, userId) => {
  check(mapServerElementId, String);
  check(userId, String);
  try {
    checkAuthentication();
    const copyMapServerElementData = MapServerElements.findOne({ _id: mapServerElementId });
    delete copyMapServerElementData.owner;
    delete copyMapServerElementData._id;
    delete copyMapServerElementData.createdAt;
    delete copyMapServerElementData.updatedAt;
    delete copyMapServerElementData.deleted;
    copyMapServerElementData.name += ' - copy';
    const copyMapServerElementId = MapServerElements.insert({
      owner: userId,
      ...copyMapServerElementData
    });
    return copyMapServerElementId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes the map server element in the database.
 *
 * @param {string} mapServerElementId - The map server element id.
 * @returns {string} Return the id of the mapServerElement.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDelete = mapServerElementId => {
  check(mapServerElementId, String);
  try {
    checkAuthentication();
    return MapServerElements.update(mapServerElementId, {
      $set: { deleted: new Date().toISOString() }
    });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the map server element in the database.
 *
 * @param {string} mapServerElementId - The map server element id.
 * @returns {string} Return the id of the map server element.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restore = mapServerElementId => {
  check(mapServerElementId, String);
  try {
    checkAuthentication();
    return MapServerElements.update(mapServerElementId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the map server element from database.
 *
 * @param {string} mapServerElementId - The map server element id.
 * @returns {string} Return the id of the deleted map server element.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDelete = mapServerElementId => {
  check(mapServerElementId, String);
  try {
    checkAuthentication();
    return MapServerElements.remove(mapServerElementId);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Gets the URL list for a layer server type or the type list.
 *
 * @param {string} typeId - The type id.
 * @returns {Array} Return the array of URLs or types.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const getTypeList = async (typeId = null) => {
  try {
    checkAuthentication();
    const input = isNil(typeId) ? 'layerServer_types' : 'layerServer_library';
    const response = HTTP.post(MAP_SERVER_ELEMENT_LIST_API, {
      headers: {
        'Content-Type': 'application/json'
      },
      data: { user: Meteor.userId(), input, id_layerserver_type: typeId }
    });
    let mapServerElementList = isHTTPResultOK(response);
    if (isString(mapServerElementList)) {
      mapServerElementList = mapServerElementList.replace(/(\r\n|\n|\r)/g, '');
      mapServerElementList = JSON.parse(mapServerElementList);
    }
    return mapServerElementList;
  } catch (error) {
    handleMethodException(error);
  }
};
